/**
 * BusinessDiscovery — "Find the business you're pitching"
 *
 * Entry: paste URL, search Google Maps, or auto-filled from lead.
 * Reuses BusinessSearchCard shell + PlacesAutocomplete from Creative Studio.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Globe, MapPin } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import DealHeader from '@/components/deal/DealHeader';
import BusinessSearchCard from '@/components/BusinessSearchCard';
import PlacesAutocomplete from '@/components/PlacesAutocomplete';
import useDealFlow from '@/hooks/useDealFlow';
import { generateDealId } from '@/components/deal/mockDealData';
import { useOrganization } from '@/hooks/useOrganization';

const SEARCH_TABS = [
  { id: 'maps', label: 'Google Maps', icon: MapPin },
  { id: 'url', label: 'Website URL', icon: Globe },
];

export default function BusinessDiscovery() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();
  const { createAdvertiser, currentOrg } = useOrganization();

  const [tab, setTab] = useState('maps');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [resolving, setResolving] = useState(false);

  // Ensure we have a dealId
  useEffect(() => {
    if (!deal.dealId) {
      const id = generateDealId();
      deal.setDealId(id);
      deal.setReturnTo(`/org/${orgSlug}/leads`);
    }
    deal.setCurrentStage('discovery');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSource = tab === 'maps' ? !!selectedBusiness : !!websiteUrl.trim();

  const handlePlaceSelect = (businessInfo) => {
    setSelectedBusiness(businessInfo);
  };

  const handleResolve = () => {
    if (!hasSource) {
      toast.error(tab === 'maps' ? 'Please select a business from search' : 'Please enter a URL');
      return;
    }

    setResolving(true);

    const buildOriginPayload = () => {
      if (tab === 'maps' && selectedBusiness) {
        return {
          place_id: selectedBusiness.placeId || null,
          website_url: selectedBusiness.website || null,
          name: selectedBusiness.name,
          address: selectedBusiness.address || '',
          location_geo: selectedBusiness.location || null,
          category: selectedBusiness.types?.[0] || 'Local Business',
          photos: selectedBusiness.photos?.length || 0,
          reviews: selectedBusiness.userRatingsTotal || 0,
          rating: selectedBusiness.rating || 0,
        };
      }

      const url = websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`;
      let name;
      try {
        name = new URL(url).hostname.replace('www.', '').split('.')[0];
        name = name.charAt(0).toUpperCase() + name.slice(1);
      } catch {
        name = websiteUrl;
      }

      return {
        place_id: null,
        website_url: url,
        name,
        address: '',
        location_geo: null,
        category: 'Local Business',
        photos: 0,
        reviews: 0,
        rating: 0,
      };
    };

    const run = async () => {
      if (!currentOrg?.id) {
        toast.error('Select an organization before adding a business.');
        setResolving(false);
        return;
      }

      try {
        const originJson = buildOriginPayload();
        const advertiser = await createAdvertiser({ name: originJson.name });
        await deal.ensureWorkspaceForAdvertiser(advertiser.id, originJson, {
          setActive: true,
          lightweight: true,
        });
        navigate(`/org/${orgSlug}/deal/business/${advertiser.id}`);
      } catch (error) {
        console.error('Failed to create business workspace', error);
        toast.error('Could not create the business workspace');
      } finally {
        setResolving(false);
      }
    };

    run();
  };

  return (
    <>
      <DealHeader
        businessName={null}
        currentStage="discovery"
        onBack={() => navigate(`/org/${orgSlug}/leads`)}
      />

      <BusinessSearchCard
        title="Find the business"
        subtitle="Paste a site or pick the business on Maps. We'll build everything else."
        tabs={SEARCH_TABS}
        activeTab={tab}
        onTabChange={setTab}
        continuationLabel={selectedBusiness && tab === 'maps' ? `Continue as ${selectedBusiness.name}` : 'Build business profile'}
        ctaLoadingLabel="Building profile..."
        ctaDisabled={!hasSource}
        isLoading={resolving}
        onSubmit={handleResolve}
        disabledTooltip={!hasSource ? (tab === 'maps' ? 'Search and select a business' : 'Enter a website URL') : null}
        footerSlot={
          <p className="text-xs text-center text-muted-foreground pt-2 opacity-50">
            Powered by AI &bull; Instant Profile Generation
          </p>
        }
      >
        {/* Google Maps Search */}
        {tab === 'maps' && (
          <div className="relative group">
            <div className="w-full">
              <APIProvider
                apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                version="weekly"
                loading="async"
              >
                <div className="relative">
                  <PlacesAutocomplete
                    onPlaceSelect={handlePlaceSelect}
                    placeholder="Enter business name..."
                    className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shadow-sm transition-shadow group-hover:shadow-md"
                  />
                </div>
              </APIProvider>
            </div>
          </div>
        )}

        {/* Website URL Input */}
        {tab === 'url' && (
          <div className="relative group">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Globe className="size-5" />
            </div>
            <input
              type="url"
              className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background pl-12 pr-4 text-base shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral"
              placeholder="https://mybusiness.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleResolve()}
              aria-label="Business website URL"
            />
          </div>
        )}
      </BusinessSearchCard>
    </>
  );
}
